import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiPlus,
  FiDownload,
  FiFilter,
  FiSearch,
  FiFileText,
  FiDollarSign,
  FiAlertCircle,
  FiCheckCircle,
  FiEye,
  FiPrinter,
  FiRefreshCw,
  FiSend,
  FiX,
  FiLoader,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

export default function SalesInvoiceList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [paymentStatus, setPaymentStatus] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [issuingId, setIssuingId] = useState(null);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadInvoices = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/sales-invoices", {
        accessToken,
      });

      setInvoices(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(requestError.message || "Could not load sales invoices.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const normalizedInvoices = useMemo(
    () =>
      invoices.map((invoice) => {
        const totalAmount = Number(invoice.totalAmount || 0);
        const paidAmount = Number(invoice.paidAmount || 0);
        const creditedAmount = Number(invoice.creditedAmount || 0);

        const balanceAmount = Math.max(
          totalAmount - paidAmount - creditedAmount,
          0,
        );

        const calculatedPaymentStatus = getPaymentStatus(
          invoice.paymentStatus,
          invoice.dueDate,
          balanceAmount,
        );

        return {
          ...invoice,
          totalAmount,
          paidAmount,
          creditedAmount,
          balanceAmount,
          displayStatus: toTitle(invoice.status),
          displayPaymentStatus: calculatedPaymentStatus,
        };
      }),
    [invoices],
  );

  const filteredInvoices = useMemo(() => {
    const query = search.trim().toLowerCase();

    return normalizedInvoices.filter((invoice) => {
      const matchesSearch =
        !query ||
        invoice.invoiceCode?.toLowerCase().includes(query) ||
        invoice.salesOrder?.salesOrderNumber?.toLowerCase().includes(query) ||
        invoice.customerNameSnapshot?.toLowerCase().includes(query) ||
        invoice.customer?.phone?.toLowerCase().includes(query);

      const matchesStatus =
        status === "All" || invoice.status === status.toUpperCase();

      const matchesPayment =
        paymentStatus === "All" ||
        invoice.displayPaymentStatus === paymentStatus;

      return matchesSearch && matchesStatus && matchesPayment;
    });
  }, [normalizedInvoices, search, status, paymentStatus]);

  const stats = useMemo(
    () => ({
      total: normalizedInvoices.length,
      paid: normalizedInvoices.filter(
        (invoice) => invoice.displayPaymentStatus === "Paid",
      ).length,
      partial: normalizedInvoices.filter(
        (invoice) => invoice.displayPaymentStatus === "Partial",
      ).length,
      unpaid: normalizedInvoices.filter(
        (invoice) =>
          invoice.displayPaymentStatus === "Unpaid" ||
          invoice.displayPaymentStatus === "Overdue",
      ).length,
    }),
    [normalizedInvoices],
  );

  const issueInvoice = async (invoice) => {
    const confirmed = window.confirm(
      `Issue ${invoice.invoiceCode}? Issued invoices cannot be cancelled directly.`,
    );

    if (!confirmed) return;

    setIssuingId(invoice.id);
    setError("");

    try {
      await apiRequest(`/sales-invoices/${invoice.id}/issue`, {
        method: "POST",
        accessToken,
      });

      await loadInvoices();
    } catch (requestError) {
      setError(requestError.message || "Could not issue this invoice.");
    } finally {
      setIssuingId(null);
    }
  };

  const exportInvoices = () => {
    const rows = [
      [
        "Invoice Number",
        "Invoice Date",
        "Sales Order",
        "Customer",
        "Amount",
        "Paid",
        "Balance",
        "Payment Status",
        "Invoice Status",
      ],
      ...filteredInvoices.map((invoice) => [
        invoice.invoiceCode,
        formatDate(invoice.invoiceDate),
        invoice.salesOrder?.salesOrderNumber || "",
        invoice.customerNameSnapshot,
        invoice.totalAmount,
        invoice.paidAmount,
        invoice.balanceAmount,
        invoice.displayPaymentStatus,
        invoice.displayStatus,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `sales-invoices-${today()}.csv`;
    anchor.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-start">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Sales Invoices</h1>

          <p className="text-gray-500 mt-2">
            Create invoices from delivered sales orders and track collection.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={exportInvoices}
            className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2 hover:bg-gray-50"
          >
            <FiDownload />
            Export
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 hover:bg-primary-800"
          >
            <FiPlus />
            Create Invoice
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Total Invoices"
          value={stats.total}
          icon={<FiFileText size={24} />}
          color="bg-blue-100 text-blue-700"
        />
        <StatCard
          title="Paid"
          value={stats.paid}
          icon={<FiCheckCircle size={24} />}
          color="bg-green-100 text-green-700"
        />
        <StatCard
          title="Partial"
          value={stats.partial}
          icon={<FiDollarSign size={24} />}
          color="bg-yellow-100 text-yellow-700"
        />
        <StatCard
          title="Unpaid / Overdue"
          value={stats.unpaid}
          icon={<FiAlertCircle size={24} />}
          color="bg-red-100 text-red-700"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6">
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search invoice, sales order, customer..."
              className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 focus:outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4 bg-white"
          >
            <option value="All">All Invoice Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Issued">Issued</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <select
            value={paymentStatus}
            onChange={(event) => setPaymentStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4 bg-white"
          >
            <option value="All">All Payment Statuses</option>
            <option value="Paid">Paid</option>
            <option value="Partial">Partial</option>
            <option value="Unpaid">Unpaid</option>
            <option value="Overdue">Overdue</option>
          </select>

          <button
            onClick={() => {
              setSearch("");
              setStatus("All");
              setPaymentStatus("All");
            }}
            className="h-12 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center gap-2"
          >
            <FiFilter />
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-5 border-b">
          <div>
            <h2 className="text-2xl font-bold">Sales Invoices</h2>
            <p className="text-gray-500 mt-1">
              Showing {filteredInvoices.length} of {normalizedInvoices.length}{" "}
              invoices
            </p>
          </div>

          <button
            onClick={loadInvoices}
            disabled={isLoading}
            className="h-11 px-5 rounded-xl border border-gray-200 flex items-center gap-2 disabled:opacity-60"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1250px]">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Invoice No.</TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader>Sales Order</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Due Date</TableHeader>
                <TableHeader right>Amount</TableHeader>
                <TableHeader right>Paid</TableHeader>
                <TableHeader right>Balance</TableHeader>
                <TableHeader center>Payment</TableHeader>
                <TableHeader center>Status</TableHeader>
                <TableHeader center>Actions</TableHeader>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="11" className="py-20 text-center text-gray-500">
                    <FiLoader className="inline animate-spin mr-2" />
                    Loading invoices...
                  </td>
                </tr>
              ) : filteredInvoices.length ? (
                filteredInvoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="px-5 py-5">
                      <div>
                        <h3 className="font-semibold">{invoice.invoiceCode}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          #{invoice.invoiceNumber}
                        </p>
                      </div>
                    </td>

                    <td className="px-5 py-5">
                      {formatDate(invoice.invoiceDate)}
                    </td>

                    <td className="px-5 py-5">
                      <span className="font-medium text-primary-700">
                        {invoice.salesOrder?.salesOrderNumber || "—"}
                      </span>
                    </td>

                    <td className="px-5 py-5">
                      <div>
                        <h3 className="font-semibold">
                          {invoice.customerNameSnapshot}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {invoice.customerPhoneSnapshot || "No phone"}
                        </p>
                      </div>
                    </td>

                    <td className="px-5 py-5">
                      {invoice.dueDate ? formatDate(invoice.dueDate) : "—"}
                    </td>

                    <td className="px-5 py-5 text-right font-semibold">
                      {formatMoney(invoice.totalAmount)}
                    </td>

                    <td className="px-5 py-5 text-right text-green-600 font-semibold">
                      {formatMoney(invoice.paidAmount)}
                    </td>

                    <td className="px-5 py-5 text-right text-red-600 font-semibold">
                      {formatMoney(invoice.balanceAmount)}
                    </td>

                    <td className="px-5 py-5 text-center">
                      <PaymentBadge status={invoice.displayPaymentStatus} />
                    </td>

                    <td className="px-5 py-5 text-center">
                      <InvoiceStatusBadge status={invoice.displayStatus} />
                    </td>

                    <td className="px-5 py-5">
                      <div className="flex justify-center gap-2">
                        <button
                          title="View invoice"
                          onClick={() =>
                            navigate(
                              `/admindashboard/sales/invoice/${invoice.id}`,
                            )
                          }
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-primary-700 hover:text-white transition"
                        >
                          <FiEye />
                        </button>

                        <button
                          title="Print invoice"
                          onClick={() => window.print()}
                          className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-green-600 hover:text-white transition"
                        >
                          <FiPrinter />
                        </button>

                        {invoice.status === "DRAFT" && (
                          <button
                            title="Issue invoice"
                            onClick={() => issueInvoice(invoice)}
                            disabled={issuingId === invoice.id}
                            className="h-9 px-3 rounded-lg bg-primary-700 text-white text-xs flex items-center gap-1 disabled:opacity-60"
                          >
                            {issuingId === invoice.id ? (
                              <FiLoader className="animate-spin" />
                            ) : (
                              <FiSend />
                            )}
                            Issue
                          </button>
                        )}

                        {invoice.status === "ISSUED" && (
                          <button
                            onClick={() =>
                              navigate("/admindashboard/sales/payment", {
                                state: {
                                  invoiceId: invoice.id,
                                  invoiceCode: invoice.invoiceCode,
                                  customerName: invoice.customerNameSnapshot,
                                  balanceAmount: invoice.balanceAmount,
                                },
                              })
                            }
                            className="h-9 px-3 rounded-lg bg-primary-700 text-white text-xs whitespace-nowrap"
                          >
                            Receive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="py-20 text-center">
                    <FiFileText
                      size={32}
                      className="mx-auto text-gray-300 mb-3"
                    />
                    <p className="font-medium text-gray-700">
                      No invoices found
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Create one from a delivered sales order.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateInvoiceModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        accessToken={accessToken}
        onCreated={(invoice) => {
          setShowCreateModal(false);
          navigate(`/admindashboard/sales/invoice/${invoice.id}`);
        }}
        setParentError={setError}
      />
    </div>
  );
}

function CreateInvoiceModal({
  open,
  onClose,
  accessToken,
  onCreated,
  setParentError,
}) {
  const [orders, setOrders] = useState([]);
  const [salesOrderId, setSalesOrderId] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const loadOrders = useCallback(async () => {
    setIsLoadingOrders(true);
    setError("");

    try {
      const response = await apiRequest(
        "/sales-invoices/eligible-sales-orders",
        { accessToken },
      );

      setOrders(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(
        requestError.message || "Could not load delivered sales orders.",
      );
    } finally {
      setIsLoadingOrders(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (open) {
      loadOrders();
    }
  }, [open, loadOrders]);

  const createInvoice = async (event) => {
    event.preventDefault();
    setError("");

    if (!salesOrderId) {
      setError("Please select a delivered sales order.");
      return;
    }

    setIsSaving(true);

    try {
      const invoice = await apiRequest("/sales-invoices", {
        method: "POST",
        accessToken,
        body: {
          salesOrderId,
          invoiceDate,
          dueDate: dueDate || undefined,
          notes: notes.trim() || undefined,
        },
      });

      onCreated(invoice);
    } catch (requestError) {
      const message = requestError.message || "Could not create invoice.";
      setError(message);
      setParentError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center">
      <form
        onSubmit={createInvoice}
        className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="px-7 py-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">Create Sales Invoice</h2>
            <p className="text-sm text-gray-500 mt-1">
              Select a fully delivered sales order.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          >
            <FiX />
          </button>
        </div>

        <div className="p-7 space-y-5">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivered Sales Order *
            </label>

            <select
              value={salesOrderId}
              onChange={(event) => setSalesOrderId(event.target.value)}
              disabled={isLoadingOrders}
              className="w-full h-12 px-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-primary-700"
            >
              <option value="">
                {isLoadingOrders
                  ? "Loading delivered sales orders..."
                  : "Select Sales Order"}
              </option>

              {orders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.salesOrderNumber} — {order.customer?.fullName} —{" "}
                  {formatMoney(order.totalAmount)}
                </option>
              ))}
            </select>

            {!isLoadingOrders && !orders.length && (
              <p className="text-sm text-gray-500 mt-2">
                No fully delivered sales orders are waiting for an invoice.
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              label="Invoice Date"
              type="date"
              value={invoiceDate}
              onChange={setInvoiceDate}
            />

            <Field
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={setDueDate}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>

            <textarea
              rows="4"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional invoice notes..."
              className="w-full rounded-xl border border-gray-200 p-4 resize-none focus:outline-none focus:border-primary-700"
            />
          </div>
        </div>

        <div className="px-7 py-5 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-5 rounded-xl border border-gray-200"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSaving || !orders.length}
            className="h-11 px-5 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
          >
            {isSaving && <FiLoader className="animate-spin" />}
            Create Draft
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, type, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-primary-700"
      />
    </div>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h2 className="text-3xl font-bold mt-3">{value}</h2>
        </div>
        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function PaymentBadge({ status }) {
  const colors = {
    Paid: "bg-green-100 text-green-700",
    Partial: "bg-yellow-100 text-yellow-700",
    Unpaid: "bg-red-100 text-red-700",
    Overdue: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${colors[status]}`}
    >
      {status}
    </span>
  );
}

function InvoiceStatusBadge({ status }) {
  const colors = {
    Draft: "bg-gray-100 text-gray-700",
    Issued: "bg-blue-100 text-blue-700",
    Cancelled: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${colors[status]}`}
    >
      {status}
    </span>
  );
}

function getPaymentStatus(paymentStatus, dueDate, balanceAmount) {
  if (paymentStatus === "PAID" || balanceAmount <= 0) return "Paid";
  if (paymentStatus === "PARTIALLY_PAID") return "Partial";

  if (dueDate && new Date(`${dueDate}T23:59:59`) < new Date()) {
    return "Overdue";
  }

  return "Unpaid";
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
